import React, { FunctionComponent, useState, useEffect, useRef, useCallback, MouseEvent } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import cx from 'classnames'
import MediaQuery from 'react-responsive'

import { DesktopCarousel, MobileCarousel } from './CarouselImpl'

const style = require('./Carousel.css')

const DESKTOP_BREAK_POINT = 973
const MOBILE_BREAK_POINT = 972
const ESC_KEY = 27
const LEFT_ARROW_KEY = 37
const RIGHT_ARROW_KEY = 39

export interface imgUrls {
  url: string
  fullUrl: string
}

interface Props{
  closeCarousel: () => void,
  images: Array<imgUrls>,
  open: boolean,
  index: number
}

const CarouselComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  closeCarousel,
  images,
  open,
  index
}) => {
  const selectedThumbnail = useRef(null)
  const [currentImage, setCurrentImage] = useState(index || 0)

  const keydown = useCallback(event => {
    if (event.keyCode === ESC_KEY) {
      closeCarousel()
    }
    else if (event.keyCode === LEFT_ARROW_KEY) {
      prevImage(event)
    }
    else if (event.keyCode === RIGHT_ARROW_KEY) {
      nextImage(event)
    }
  }, [currentImage])

  useEffect(() => {
    window.addEventListener('scroll', onscroll, false)
    document.addEventListener('keydown', keydown, false)

    return () => {
      window.removeEventListener('scroll', onscroll, false)
      document.removeEventListener('keydown', keydown, false)
    }
  }, [keydown])

  const onscroll = () => {
    if (open && window.pageYOffset > 0) {
      closeCarousel()
    }
  }

  useEffect(() => {
    const div = selectedThumbnail.current
    if (div) {
      div.scrollIntoView()
      window.scrollTo(0, 0)
    }
  }, [currentImage])

  const selectThumbnail = (e: MouseEvent, idx: number) => {
    e.stopPropagation()
    setCurrentImage(idx)
  }

  const nextImage = (e: MouseEvent) => {
    e.stopPropagation()
    setCurrentImage(currentImage === images.length - 1 ? 0 : currentImage + 1)
  }

  const prevImage = (e: MouseEvent) => {
    e.stopPropagation()
    setCurrentImage(currentImage === 0 ? images.length - 1 : currentImage - 1)
  }

  const getClassName = (idx : number) => {
    return cx(style.thumbnail__wrapper, {
      [style['thumbnail--selected']]: idx === currentImage
    })
  }

  const getRef = (idx : number) => (idx === currentImage) ? selectedThumbnail : null

  const renderThumbnails = () => {
    return (
      <div className={style.gallery}>
        {images.map((value, idx) => {
          return (
            <div key={idx} className={getClassName(idx)} ref={getRef(idx)} >
              <img className={style.thumbnail} src={value.url} onClick={(e) => selectThumbnail(e, idx)} />
            </div>
          )
        })}
      </div>
    )
  }

  if (!open) {
    return null
  }

  return (
    <div>
      <MediaQuery minWidth={DESKTOP_BREAK_POINT} values={{ width: window.innerWidth }}>
        <DesktopCarousel
          currentImage={currentImage}
          images={images}
          renderThumbnails={renderThumbnails}
          onClose={closeCarousel}
          prevImage={prevImage}
          nextImage={nextImage}
        />
      </MediaQuery>
      <MediaQuery maxWidth={MOBILE_BREAK_POINT} values={{ width: window.innerWidth }}>
        <MobileCarousel
          currentImage={currentImage}
          images={images}
          onClose={closeCarousel}
          prevImage={prevImage}
          nextImage={nextImage}
        />
      </MediaQuery>
    </div>
  )

}

export const Carousel = injectIntl(CarouselComponent)
